// ai-sidebar/components/command-menu.tsx
// The "/" command palette (state 7). Presentational menu styled to Direction D
// (frameCommand): a popover above the composer with Do / Ask / Manage groups.
// Keyboard nav + selection are owned by the Composer; this renders the list.
import { Wand2, FileText, BarChart3, MessageSquare, Bookmark, Clock, Search, CornerDownLeft } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type CommandAction =
  | { type: "prompt"; prompt: string }
  | { type: "save-conversation" }
  | { type: "history" };

export interface CommandDef {
  group: "Do" | "Ask" | "Manage";
  icon: LucideIcon;
  label: string;
  action: CommandAction;
}

export const COMMANDS: CommandDef[] = [
  { group: "Do", icon: Wand2, label: "Surface the key insight here", action: { type: "prompt", prompt: "What's the most important insight here?" } },
  { group: "Do", icon: FileText, label: "Write an email summary for leadership", action: { type: "prompt", prompt: "Write an email summary of this for leadership." } },
  { group: "Do", icon: BarChart3, label: "Turn this into slide ideas", action: { type: "prompt", prompt: "Turn this into slide ideas I can present." } },
  { group: "Ask", icon: MessageSquare, label: "Ask about this page", action: { type: "prompt", prompt: "What does this page show me?" } },
  { group: "Manage", icon: Bookmark, label: "Save conversation to insights", action: { type: "save-conversation" } },
  { group: "Manage", icon: Clock, label: "View conversation history", action: { type: "history" } },
];

export function filterCommands(query: string): CommandDef[] {
  const q = query.trim().toLowerCase();
  return q ? COMMANDS.filter((c) => c.label.toLowerCase().includes(q)) : COMMANDS;
}

export function CommandMenu({
  query,
  items,
  activeIndex,
  onSelect,
}: {
  query: string;
  items: CommandDef[];
  activeIndex: number;
  onSelect: (index: number) => void;
}) {
  const groups: CommandDef["group"][] = ["Do", "Ask", "Manage"];
  return (
    <div
      className="absolute z-10 overflow-hidden bg-popover"
      style={{
        left: 18,
        right: 18,
        bottom: 86,
        border: "1px solid var(--border)",
        borderRadius: 14,
        boxShadow: "var(--elevation-lg)",
        maxHeight: 440,
      }}
    >
      <div className="flex items-center gap-2.5" style={{ padding: "12px 14px", borderBottom: "1px solid var(--border)" }}>
        <Search className="w-4 h-4 shrink-0 text-muted-foreground" style={{ strokeWidth: 1.85 }} />
        <span className="flex-1 text-foreground" style={{ fontSize: 13.5 }}>
          {query || <span className="text-muted-foreground">Type a command…</span>}
        </span>
        <span className="ai-caret inline-block" style={{ width: 1.5, height: 15, background: "var(--spectral-violet)" }} />
      </div>
      <div style={{ padding: "4px 6px 8px" }}>
        {items.length === 0 && (
          <div className="text-muted-foreground" style={{ padding: "9px 12px", fontSize: 13 }}>No matching commands</div>
        )}
        {groups.map((g) => {
          const groupItems = items.filter((it) => it.group === g);
          if (groupItems.length === 0) return null;
          return (
            <div key={g}>
              <div
                className="uppercase text-muted-foreground"
                style={{ padding: "10px 12px 5px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em" }}
              >
                {g}
              </div>
              {groupItems.map((it) => {
                const idx = items.indexOf(it);
                const active = idx === activeIndex;
                const Icon = it.icon;
                return (
                  <button
                    key={it.label}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); onSelect(idx); }}
                    className="w-full flex items-center gap-2.5 text-left"
                    style={{ padding: "9px 12px", borderRadius: 9, background: active ? "var(--spectral-violet-10)" : "transparent" }}
                  >
                    <span
                      className="flex items-center justify-center shrink-0"
                      style={{ width: 26, height: 26, borderRadius: 7, background: active ? "var(--card)" : "var(--muted)" }}
                    >
                      <Icon style={{ width: 14, height: 14, strokeWidth: 1.85, color: active ? "var(--spectral-violet)" : "var(--muted-foreground)" }} />
                    </span>
                    <span className="flex-1 text-foreground" style={{ fontSize: 13, fontWeight: 500 }}>{it.label}</span>
                    {active && <CornerDownLeft className="w-3.5 h-3.5 text-muted-foreground" style={{ strokeWidth: 1.85 }} />}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
